import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { IntlProvider } from "react-intl-next";

import {
    JiraModalBackground,
    PreviewContainer,
    type Initials
} from "@atlassian/product-setup";

import { getFirstName, getLastName } from "@/components/signup/utils";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { StepContext, StepProps } from "../../../types";

import { ArrowSvg } from "./arrow-svg";

const previewStyles = css({
    "position": "relative",
    "@media (max-width: 1023px)": {
        display: "none"
    }
});

const previewCardStyles = css({
    position: "absolute",
    insetInlineStart: "64px",
    insetBlockStart: "24px",
    zIndex: 2
});

const modalBackgroundStyles = css({
    position: "absolute",
    insetBlockStart: "-64px"
});

export const Preview = <T extends StepContext>({
    context,
    screenId
}: StepProps<T>) => {
    const { locale } = useIntl();
    const loggedInUser = useLoggedInUser();
    const shouldShowArrow = screenId === "space-details";
    const shouldShowCollaborationCursors =
        screenId === "invite" || screenId === "loading";
    let userInitials = "";
    if (loggedInUser.name) {
        const firstInital = getFirstName(loggedInUser.name).charAt(0);
        const lastInitial = getLastName(loggedInUser.name).charAt(0);
        userInitials = firstInital + lastInitial;
    }

    return (
        <IntlProvider locale={locale}>
            <div css={previewStyles}>
                <div css={modalBackgroundStyles}>
                    <JiraModalBackground />
                </div>
                <div css={previewCardStyles}>
                    {shouldShowArrow && (
                        <div
                            css={css({
                                position: "absolute",
                                insetInlineEnd: "90px",
                                insetBlockStart: "-87px"
                            })}
                        >
                            <ArrowSvg />
                        </div>
                    )}
                    <PreviewContainer
                        spaceName={context.spaceName ?? ""}
                        userInitials={userInitials as Initials}
                        previewCardData={{
                            previewType: context.template
                        }}
                        shouldShowCollaborationCursors={
                            shouldShowCollaborationCursors
                        }
                    />
                </div>
            </div>
        </IntlProvider>
    );
};
